import "./Header.css"
import { useState, useEffect, useCallback } from 'react';
import SearchBar from './SearchBar'
import logoBlack from "../images/logoBlack.svg"
import logoWhite from "../images/logoWhite.png"
import userBlack from "../images/userBlack.svg"
import userWhite from "../images/userWhite.svg"
import favoriteBlack from "../images/favoriteBlack.svg"
import favoriteWhite from "../images/favoriteWhite.svg"
import chatBlack from "../images/chatBlack.svg"
import chatWhite from "../images/chatWhite.svg"
import addBlack from "../images/addBlack.svg"
import addWhite from "../images/addWhite.svg"
import notificationsBlack from "../images/notificationsBlack.svg"
import notificationsWhite from "../images/notificationsWhite.svg"
import basketBlack from "../images/basketBlack.svg"
import basketWhite from "../images/basketWhite.svg"
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from "../context/DarkModeContext";
import { useNavigate } from "react-router-dom";
import api from '../api/client';
import { getNotificationSocket, getSocket } from '../api/socket';

const Header = ({ translations }) => {
    const { user } = useAuth();
    const { darkMode } = useDarkMode();
    const navigate = useNavigate();

    const [unread, setUnread] = useState(0);
    const [basketSize, setBasketSize] = useState(0);
    const [dmUnread, setDmUnread] = useState(0);

    const refreshBasket = useCallback(() => {
        if (!user) return;
        api.getBasketSize()
            .then((n) => setBasketSize(Number(n) || 0))
            .catch(() => {});
    }, [user]);

    const refreshDm = useCallback(() => {
        if (!user) return;
        api.getDiscussionsUnreadCount()
            .then((r) => setDmUnread(r?.count || 0))
            .catch(() => {});
    }, [user]);

    useEffect(() => {
        if (!user) { setUnread(0); setBasketSize(0); setDmUnread(0); return; }
        api.getUnreadNotificationCount().then((r) => setUnread(r?.count || 0)).catch(() => {});
        refreshBasket();
        refreshDm();
    }, [user, refreshBasket, refreshDm]);

    // Live unread badge via the /notifications socket namespace.
    useEffect(() => {
        if (!user) return undefined;
        const socket = getNotificationSocket();
        if (!socket) return undefined;
        const onCount = (payload) => setUnread(payload?.count ?? 0);
        const onNew = () => setUnread((c) => c + 1);
        socket.on('notification:unread-count', onCount);
        socket.on('notification:new', onNew);
        return () => {
            socket.off('notification:unread-count', onCount);
            socket.off('notification:new', onNew);
        };
    }, [user]);

    // Basket size updates when items are added/removed anywhere in the app.
    useEffect(() => {
        const onBasketChange = () => refreshBasket();
        window.addEventListener('basket:changed', onBasketChange);
        return () => window.removeEventListener('basket:changed', onBasketChange);
    }, [refreshBasket]);

    // Live DM unread badge: bump on incoming messages (discussions gateway),
    // and refetch when the Messages page marks a conversation as read.
    useEffect(() => {
        if (!user) return undefined;
        const socket = getSocket();
        const onReceived = () => refreshDm();
        const onRead = () => refreshDm();
        socket?.on('message:received', onReceived);
        window.addEventListener('dm:changed', onRead);
        return () => {
            socket?.off('message:received', onReceived);
            window.removeEventListener('dm:changed', onRead);
        };
    }, [user, refreshDm]);

    function go(path) {
        if (user) navigate(path);
        else navigate("/login");
    }

    return (
        <div className='container'>
            <nav className="navbar">
                <SearchBar translations={translations} />
                <div className='nav-center'>
                    <a href="/">
                        <img className="logo-img" src={darkMode ? logoWhite : logoBlack} alt="logo"></img>
                    </a>
                </div>
                <div className='nav-right'>
                    <ul>
                        { user?.userType === "artist" && (
                            <button onClick={() => go("/create-card")} className="btn-log">
                                <img src={darkMode ? addWhite : addBlack} alt="createPost" width="45" height="45"/>
                                <p className='nav-text'>{translations['header-create']}</p>
                            </button>
                        )}
                        <button onClick={() => go("/messages")} className="btn-log nav-icon-btn">
                            <img src={darkMode ? chatWhite : chatBlack} alt="message" width="45" height="45"/>
                            {dmUnread > 0 && <span className="nav-badge">{dmUnread > 99 ? '99+' : dmUnread}</span>}
                            <p className='nav-text'>{translations['header-messages']}</p>
                        </button>
                        <button onClick={() => go("/notifications")} className="btn-log nav-icon-btn">
                            <img src={darkMode ? notificationsWhite : notificationsBlack} alt="notifications" width="45" height="45"/>
                            {unread > 0 && <span className="nav-badge">{unread > 99 ? '99+' : unread}</span>}
                            <p className='nav-text'>{translations['header-notifications'] || 'Notifications'}</p>
                        </button>
                        <button onClick={() => go("/basket")} className="btn-log nav-icon-btn">
                            <img src={darkMode ? basketWhite : basketBlack} alt="basket" width="45" height="45"/>
                            {basketSize > 0 && <span className="nav-badge">{basketSize > 99 ? '99+' : basketSize}</span>}
                            <p className='nav-text'>{translations['header-basket'] || 'Basket'}</p>
                        </button>
                        <button onClick={() => go("/favorites")} className="btn-log nav-icon-btn">
                            <img src={darkMode ? favoriteWhite : favoriteBlack} alt="favorite" width="45" height="45"/>
                            <p className='nav-text'>{translations['header-favorites']}</p>
                        </button>
                        {user?.role === 'admin' && (
                            <button onClick={() => go("/admin")} className="btn-log nav-icon-btn">
                                <span className="nav-emoji">🛡️</span>
                                <p className='nav-text'>{translations['header-admin'] || 'Admin'}</p>
                            </button>
                        )}
                        <button onClick={() => go("/profile")} className="btn-log nav-icon-btn">
                            <img src={darkMode ? userWhite : userBlack} alt="profile" width="45" height="45"/>
                            <p className='nav-text'>{translations['header-profile']}</p>
                        </button>
                    </ul>
                </div>
            </nav>
        </div>
    );
};

export default Header;
